import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Flag,
  Sparkles,
  Target,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  calendarApi,
  calendarConflictsApi,
  dailyFocusBlocksApi,
  driversApi,
  notificationsApi,
  rhythmApi,
  workItemsApi,
} from '../api/services';
import type {
  AnnualStrategicDriver,
  CalendarConflict,
  CalendarEvent,
  CapacitySummary,
  DailyFocusBlock,
  LinkedWorkItemSummary,
  WorkItem,
} from '../types/models';
import { useAuth } from '../auth/AuthProvider';
import { colourForDriverId } from '../theme/tokens';
import { DayRibbon, type DayRibbonSegment } from '@/components/dashboard/DayRibbon';
import { StrategicPulseBand } from '@/components/dashboard/StrategicPulseBand';
import { CapacityStrip } from '@/components/dashboard/CapacityStrip';
import { deriveCapacity } from '../utils/dashboard';
import { useDecisionCapture } from '../components/workItems/useDecisionCapture';
import { useToast } from '@/components/ui/toast';
import { openFocusSession } from '@/components/focus/FocusSessionFull';

// ---------------------------------------------------------------------------
// New Tailwind-based dashboard. Replaces the 1443-line MUI DashboardPage —
// the legacy MUI version was deleted; check git history for the prior surface.
//
// Layout: SlimRail (left, owned by AppLayout) ⨯ main grid:
//   - Top header: greeting, date, AI Insights pill
//   - Hero: 5-day Daily Activity Timeline (calendar + focus blocks rendered
//     at minute precision, mirrors FocusTimePage's grid behaviour)
//   - Bottom-left: Resource Planning (capacity gauge with optimal range)
//   - Bottom-mid: My Work — Active items, coloured by strategic driver
//   - Right rail (xl+): Strategic Pulse (per-driver progress) + Day Tuning
//     feed (coaching insights + calendar conflicts consolidated, with
//     Resolve / Ignore actions)
// ---------------------------------------------------------------------------

const TIMELINE_DAY_START_HOUR = 7;
const TIMELINE_DAY_END_HOUR = 19;

function formatDateOnly(d: Date): string {
  // Local-date components — same fix as FocusTimePage avoids the BST
  // off-by-one we shipped against last week.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function minutesFromDayStart(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h - TIMELINE_DAY_START_HOUR) * 60 + (m || 0);
}

function minutesFromDayStartDate(d: Date): number {
  return (d.getHours() - TIMELINE_DAY_START_HOUR) * 60 + d.getMinutes();
}

// Mirrors FocusSessionCard.tsx:83-86 — true when `now` falls inside the
// [startTime, endTime) window of the focus block on its `date`. Compared
// in local minute-of-day so we don't have to reconstruct a Date for the
// block boundaries on every tick.
function isCurrentlyInside(block: DailyFocusBlock, now: Date): boolean {
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (block.date !== todayKey) return false;
  const minuteOfDay = (hhmmss: string): number => {
    const [h = '0', m = '0'] = hhmmss.split(':');
    return parseInt(h, 10) * 60 + parseInt(m, 10);
  };
  const cur = now.getHours() * 60 + now.getMinutes();
  return minuteOfDay(block.startTime) <= cur && minuteOfDay(block.endTime) > cur;
}


interface DayTuningEntry {
  id: string;
  kind: 'coaching' | 'conflict-hard' | 'conflict-soft';
  title: string;
  detail: string;
  action?: { label: string; path: string };
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const today = useMemo(() => new Date(), []);

  // Ticking `now` so the active focus-block detection updates as time
  // advances without requiring the user to refresh the page. 30 s is the
  // cheapest cadence that still flips the "Currently focusing on" header
  // within a minute of a block starting or ending. Lighter than the
  // FocusSessionCard's 1 Hz tick because the dashboard doesn't render a
  // live countdown — it only needs to know which block (if any) is live.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Mount fade-in — page lifts opacity + translate-y on first render.
  // 200 ms ease-out matches the redesign's calm motion budget;
  // suppressed under `html.reduced-motion` via index.css.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setMounted(true), 16);
    return () => window.clearTimeout(id);
  }, []);
  // Week window — still loaded so the weekly focus-protected KPI has a
  // 7-day denominator. The 5-day timeline grid was removed in the
  // redesign; weekDates / setWeekStart are no longer needed.
  const weekStart = useMemo(() => getMonday(new Date()), []);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const [loading, setLoading] = useState(true);
  const [capacity, setCapacity] = useState<CapacitySummary | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [focusBlocks, setFocusBlocks] = useState<DailyFocusBlock[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [coachingInsights, setCoachingInsights] = useState<
    Array<{ title: string; message: string; entityType?: string; entityId?: string }>
  >([]);
  const [conflicts, setConflicts] = useState<CalendarConflict[]>([]);
  // Dismissed Day-Tuning signatures, persisted in localStorage so an Ignore
  // sticks across page reloads and across days. The previous transient
  // useState approach cleared on refresh, which made Ignore feel broken.
  // Storage key is bumped (v2) every time the signature shape changes so old
  // entries don't ghost-suppress new ones.
  const [dismissedTuning, setDismissedTuning] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('cadence_dismissed_tuning_v2');
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? new Set<string>(parsed) : new Set();
    } catch {
      return new Set();
    }
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [capacityRes, evts, blocks, drvs, items, coach, confs] =
        await Promise.allSettled([
          rhythmApi.getCapacity(formatDateOnly(today)),
          calendarApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
          dailyFocusBlocksApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
          driversApi.getActive(),
          workItemsApi.getAll(),
          notificationsApi.getCoachingInsights(),
          calendarConflictsApi.getByDate(formatDateOnly(today)),
        ]);
      if (capacityRes.status === 'fulfilled') setCapacity(capacityRes.value);
      if (evts.status === 'fulfilled') setCalendarEvents(evts.value.filter(e => !e.isAllDay));
      if (blocks.status === 'fulfilled') setFocusBlocks(blocks.value);
      if (drvs.status === 'fulfilled') setDrivers(drvs.value);
      if (items.status === 'fulfilled') setWorkItems(items.value);
      if (coach.status === 'fulfilled') setCoachingInsights(coach.value);
      if (confs.status === 'fulfilled') setConflicts(confs.value);
    } finally {
      setLoading(false);
    }
  }, [today, weekStart, weekEnd]);

  // Decisions route through the capture prompt; everything else keeps the
  // optimistic tick. Without this, decisions completed from the Dashboard — the
  // main daily surface — would never be captured, leaving the Close Day
  // backstop to do all the work.
  const { toast } = useToast();
  const { completeWorkItem, dialog: decisionCaptureDialog } = useDecisionCapture(
    () => { void loadAll(); },
    // Without this a failed capture closes the dialog silently and leaves the
    // item incomplete with no feedback at all.
    (message) => toast({ variant: 'error', message }),
  );

  const handleCompleteWorkItem = async (id: string) => {
    const item = workItems.find(w => w.id === id);
    if (item?.type === 'Decision') {
      await completeWorkItem(item);
      return;
    }
    setWorkItems(prev => prev.map(w => (w.id === id ? { ...w, status: 'Done' } : w)));
    try {
      await workItemsApi.update(id, { status: 'Done' });
    } catch {
      void loadAll();
    }
  };

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Cross-client sync — when another client (Mac app, EA on a different
  // browser) mutates a work item, AppLayout receives the SignalR broadcast
  // and dispatches cadence:workitem-changed. Refetch the Dashboard's local
  // state so the user sees the change without an F5.
  useEffect(() => {
    const handler = () => { void loadAll(); };
    window.addEventListener('cadence:workitem-changed', handler);
    return () => window.removeEventListener('cadence:workitem-changed', handler);
  }, [loadAll]);

  const tuningEntries: DayTuningEntry[] = useMemo(() => {
    // Stable signature per entry so Ignore survives reloads. Entity-based
    // when we have one (coaching tied to a WorkItem / Driver / Project), or
    // a hash of the title text when we don't. Conflicts key off the
    // calendar event id when present, otherwise off the description.
    const entries: DayTuningEntry[] = [];
    coachingInsights.forEach(c => {
      const sig = c.entityType && c.entityId
        ? `coach:${c.entityType}:${c.entityId}`
        : `coach:title:${c.title}`;
      entries.push({
        id: sig,
        kind: 'coaching',
        title: c.title,
        detail: c.message,
        action: c.entityType
          ? {
              label: 'Open',
              path:
                c.entityType === 'WorkItem'
                  ? `/work-items?id=${c.entityId}`
                  : c.entityType === 'StrategicDriver'
                    ? `/strategy?id=${c.entityId}`
                    : c.entityType === 'Project'
                      ? `/projects/${c.entityId}`
                      : '/dashboard',
            }
          : undefined,
      });
    });
    conflicts.forEach(c => {
      const conflictKey = c.calendarEventId || c.workBlockId || `${c.startTime}|${c.description}`;
      entries.push({
        id: `conflict:${c.severity}:${conflictKey}`,
        kind: c.severity === 'Hard' ? 'conflict-hard' : 'conflict-soft',
        title: c.severity === 'Hard' ? 'Calendar conflict' : 'Soft overlap',
        detail: c.description,
        action: { label: 'Resolve', path: '/calendar' },
      });
    });
    return entries.filter(e => !dismissedTuning.has(e.id));
  }, [coachingInsights, conflicts, dismissedTuning]);

  const dismissTuning = (id: string) => {
    setDismissedTuning(prev => {
      const next = new Set(prev);
      next.add(id);
      try {
        // Array.from for ES5 target compatibility (no spread of Set).
        localStorage.setItem('cadence_dismissed_tuning_v2', JSON.stringify(Array.from(next)));
      } catch {
        // localStorage write can fail in private mode / quota — drop
        // silently; the set still works for the session.
      }
      return next;
    });
  };

  const activeItems = useMemo(() => {
    const todayKey = formatDateOnly(today);
    return workItems
      .filter(
        item =>
          (item.targetDate && item.targetDate.startsWith(todayKey)) ||
          item.status === 'InProgress',
      )
      .slice(0, 6);
  }, [today, workItems]);

  // Active focus block — the one the executive is *inside right now*.
  // When present, the "Today's priorities" card re-titles to
  // "Currently focusing on — {label}" and floats the block's
  // linkedWorkItems to the top of the list. Drives the calm "here's what
  // you're working on this hour" framing in the redesign.
  const activeBlock = useMemo(
    () => focusBlocks.find(b => isCurrentlyInside(b, now)) ?? null,
    [focusBlocks, now],
  );

  // Strip linked items out of activeItems so the same WorkItem doesn't
  // render twice (once at the top via linkedWorkItems, once below via the
  // regular day's list).
  const linkedItemIds = useMemo(() => {
    if (!activeBlock?.linkedWorkItems) return new Set<string>();
    return new Set(activeBlock.linkedWorkItems.map(w => w.id));
  }, [activeBlock]);
  const laterItems = useMemo(
    () => activeItems.filter(i => !linkedItemIds.has(i.id)),
    [activeItems, linkedItemIds],
  );

  const greeting = useMemo(() => {
    const hour = today.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }, [today]);

  // Flatten today's events + focus blocks into DayRibbonSegment[].
  // Calendar events that look like focus blocks (title matches the
  // backend's focus pattern) tile as `focus`; events marked private
  // tile as `personal` (matches the FocusTimePage convention); the
  // rest are meetings.
  const todaySegments = useMemo<DayRibbonSegment[]>(() => {
    const todayKey = formatDateOnly(today);
    const segs: DayRibbonSegment[] = [];
    // Focus blocks may have been written back to the external calendar by
    // FocusTimeAllocationService.AllocateFocusTimeAsync — in which case the
    // same time window shows up as BOTH a focus block AND a calendar event.
    // Build a set of external-event-IDs the focus blocks claim so we can
    // skip those calendar events and avoid double-counting. (Bug 2026-06-01:
    // web read "8 focus blocks today" when there were really 4, because each
    // block was counted twice.)
    const focusOwnedExternalIds = new Set<string>();
    for (const block of focusBlocks) {
      if (block.date !== todayKey) continue;
      if (block.externalCalendarEventId) focusOwnedExternalIds.add(block.externalCalendarEventId);
      if (block.linkedCalendarEventId) focusOwnedExternalIds.add(block.linkedCalendarEventId);
    }
    for (const evt of calendarEvents) {
      const dateStr = (evt.startTime ?? '').slice(0, 10);
      if (dateStr !== todayKey) continue;
      // Skip if this calendar event is the focus block's calendar twin.
      if (focusOwnedExternalIds.has(evt.id) || focusOwnedExternalIds.has((evt as any).externalId)) continue;
      const start = minutesFromDayStartDate(new Date(evt.startTime));
      const end = minutesFromDayStartDate(new Date(evt.endTime));
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
      // Categorise using the canonical isPersonalEvent flag (set by
      // CalendarSource.IsPersonal — same field the backend uses everywhere
      // for the "exclude personal events from meeting counts" filter).
      // Previously fell back to a title-regex heuristic that mis-classified
      // any meeting with "personal" / "focus" / "block" in the title.
      const kind: DayRibbonSegment['kind'] =
        (evt as any).isPersonalEvent ? 'personal' : 'meeting';
      segs.push({
        kind,
        startMinutes: start + TIMELINE_DAY_START_HOUR * 60,
        endMinutes: end + TIMELINE_DAY_START_HOUR * 60,
        title: evt.title || (kind === 'meeting' ? 'Meeting' : 'Event'),
      });
    }
    for (const block of focusBlocks) {
      if (block.date !== todayKey) continue;
      const start = minutesFromDayStart(block.startTime);
      const end = minutesFromDayStart(block.endTime);
      if (end <= start) continue;
      segs.push({
        kind: 'focus',
        startMinutes: start + TIMELINE_DAY_START_HOUR * 60,
        endMinutes: end + TIMELINE_DAY_START_HOUR * 60,
        title: block.label || 'Focus time',
      });
    }
    return segs.sort((a, b) => a.startMinutes - b.startMinutes);
  }, [calendarEvents, focusBlocks, today]);

  // Reassurance subtitle — calm one-line summary of today's calendar
  // shape, mirroring the prototype's "Four meetings and three focus
  // blocks today, with the school run and lunch held around them."
  // Counts are dynamic. Falls back to a "plenty of room" line when
  // the calendar is empty so the page never reads as broken.
  const todayReassurance = useMemo(() => {
    const meets = todaySegments.filter(s => s.kind === 'meeting').length;
    const focusCount = todaySegments.filter(s => s.kind === 'focus').length;
    if (meets === 0 && focusCount === 0) {
      return 'Nothing on the calendar yet. Plenty of room to make today yours.';
    }
    const meetingsPart =
      meets === 0 ? 'No meetings' : `${meets} meeting${meets === 1 ? '' : 's'}`;
    const focusPart =
      focusCount === 0 ? 'no protected focus yet' : `${focusCount} focus block${focusCount === 1 ? '' : 's'}`;
    return `${meetingsPart} and ${focusPart} today.`;
  }, [todaySegments]);

  // First in-progress or due-today work item — drives the "Next action"
  // callout under the day ribbon. Falls back to the soonest scheduled
  // focus block if no work item qualifies.
  const nextAction = useMemo(() => {
    const todayKey = formatDateOnly(today);
    const candidates = workItems
      .filter(w => w.status !== 'Done' && w.status !== 'Cancelled')
      .filter(w =>
        w.status === 'InProgress' ||
        (w.targetDate && w.targetDate.startsWith(todayKey)),
      );
    if (candidates.length === 0) return null;
    return candidates[0];
  }, [workItems, today]);

  // Focus-protected minutes this week + a target line.
  const weekFocusProtected = useMemo(() => {
    const minutes = focusBlocks.reduce((sum, b) => {
      const start = minutesFromDayStart(b.startTime);
      const end = minutesFromDayStart(b.endTime);
      return sum + Math.max(0, end - start);
    }, 0);
    return Math.round((minutes / 60) * 10) / 10; // hours, 1 dp
  }, [focusBlocks]);

  const focusTargetHours = 12; // soft weekly target; surfaces as the "of N hours" line

  // Strategic alignment: fraction of in-flight work items linked to a
  // driver. Calm metric — no driver-less item is itself a problem, but
  // a high alignment number signals the rhythm is connected to strategy.
  const alignmentPct = useMemo(() => {
    const live = workItems.filter(
      w => w.status === 'Planned' || w.status === 'InProgress',
    );
    if (live.length === 0) return 0;
    const aligned = live.filter(w => Boolean((w as any).strategicDriverId));
    return Math.round((aligned.length / live.length) * 100);
  }, [workItems]);

  const heroDateLabel = today.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <>
      {decisionCaptureDialog}
      {/* Calm-by-default backdrop. The previous version had four
          saturated blue/violet/amber/rose blobs at the page corners
          that bled through the SlimRail at the new 220px width and
          fought the muted card surfaces in front of them. Replaced
          with the page-level `background` token (slate in dark, off-
          white in light) so the surface reads quietly and the cards
          carry the colour through their driver tags + chips. */}

      <div
        className={cn(
          // AppLayout's <main> already pads p-3 (12px) / sm:p-4 (16px) /
          // md:p-6 (24px) around the Outlet, so we add 24px more here
          // to land the spec's flat 48px total horizontal at md+. Top
          // is pt-2 (8px) to land 32px after main's pt-6 (24px); bottom
          // is pb-12 (48px) to land 72px after main's pb-6 (24px) —
          // close to spec's 80px without leaving a hard-to-reach
          // bottom edge.
          'mx-auto flex w-full max-w-[1600px] flex-col gap-5 px-6 pt-2 pb-12 transition-[opacity,transform] duration-200 ease-out',
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1',
        )}
      >
        {/* Header — date eyebrow + greeting + reassurance on the left,
            action buttons on the right. items-start so the buttons
            align with the eyebrow at the top of the row, not the
            reassurance line at the bottom — per the prototype's
            .page-head pattern. */}
        <header className="mb-6 flex flex-wrap items-start justify-between gap-6">
          <div className="max-w-[54ch]">
            <p className="mb-1.5 text-[12px] font-medium tracking-[0.04em] text-muted-foreground">
              {heroDateLabel}
            </p>
            <h1 className="font-serif text-[clamp(30px,3.6vw,46px)] font-light leading-[1.05] tracking-[-0.015em] text-foreground">
              {greeting}
              {user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}.
            </h1>
            {/* Reassurance subtitle — calm context line composed from
                what the calendar shape actually is today. Reads as a
                "here's what to expect" sentence, not a status display. */}
            <p className="mt-[13px] max-w-[54ch] text-base font-light text-muted-foreground">
              {todayReassurance}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            {/* AI insights — ghost-style button (card surface + hairline
                border + muted text) so it reads as the lower-emphasis
                action next to the primary "Open today's plan". Clicking
                opens the planning surface where coaching, conflict
                detection and time-allocation hints surface contextually. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/plan')}
                  className="gap-[7px] border-border bg-card px-[15px] py-[9px] text-[14px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Sparkles className="h-[15px] w-[15px]" />
                  AI insights
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Coaching, conflict detection and time-allocation hints surface contextually inside the plan and dashboard cards.
              </TooltipContent>
            </Tooltip>
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/plan')}
              className="gap-[7px] px-[15px] py-[9px] text-[14px] font-semibold"
            >
              Open today's plan
              <ArrowRight className="h-[15px] w-[15px]" />
            </Button>
          </div>
        </header>

        {/* HERO — Day ribbon + one next action.
            The ribbon shows today shaped by event type (focus = glowing
            blue, meeting = neutral, personal = dashed lavender) and a
            live now-marker. Below it sits the single most important
            thing to do next — verb-first, calm, no clutter. */}
        <Card className="overflow-visible">
          <CardContent className="space-y-4 py-5">
            <div className="flex items-center justify-end gap-4 text-[11.5px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2 w-3.5 rounded-[2px] bg-gradient-to-b from-[#A9CBFF] to-primary"
                  style={{ boxShadow: '0 0 8px rgba(26,108,240,0.32)' }}
                />
                Protected focus
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-3.5 rounded-[2px] bg-foreground/40" />
                Meeting
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-3.5 rounded-[2px] border border-dashed border-[#B39DFF] bg-[#B39DFF]/[0.18]" />
                Personal
              </span>
            </div>
            <DayRibbon
              segments={todaySegments}
              startHour={TIMELINE_DAY_START_HOUR}
              endHour={TIMELINE_DAY_END_HOUR}
            />
            {nextAction && (
              <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-4">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                    Next action
                  </p>
                  <h2 className="mt-1.5 mb-2 truncate text-[19px] font-semibold tracking-[-0.01em]">
                    {nextAction.title}
                  </h2>
                  {/* Meta row — HH:MM mono start time (from the linked
                      scheduled block, falls back to derived) + driver
                      name with 8px dot + type + duration, per the
                      prototype's .hero-meta pattern. */}
                  <p className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
                    {nextAction.scheduledBlocks?.[0]?.startTime && (
                      <>
                        <span className="font-mono tabular-nums">
                          {nextAction.scheduledBlocks[0].startTime}
                        </span>
                        <span aria-hidden className="text-muted-foreground/60">·</span>
                      </>
                    )}
                    {(nextAction as any).strategicDriverId && (
                      <>
                        <span
                          aria-hidden
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ background: colourForDriverId((nextAction as any).strategicDriverId) }}
                        />
                        <span>
                          {drivers.find(d => d.id === (nextAction as any).strategicDriverId)?.title ?? 'Driver'}
                        </span>
                        <span aria-hidden className="text-muted-foreground/60">·</span>
                      </>
                    )}
                    <span>{nextAction.type}</span>
                    {nextAction.estimatedMinutes > 0 && (
                      <>
                        <span aria-hidden className="text-muted-foreground/60">·</span>
                        <span>{formatDuration(nextAction.estimatedMinutes)}</span>
                      </>
                    )}
                  </p>
                </div>
                <Button size="lg" onClick={() => openFocusSession()}>
                  Begin focus
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3-up KPI strip — capacity / focus protected / strategic alignment.
            Cards stretch to a uniform row height via the grid; each
            CardContent is a flex column so the progress bars at the
            bottom land at the same Y line across all three. Without the
            flex-col + mt-auto, the bars were drifting up/down because
            the "headline number" rows have different intrinsic heights
            (26pt mono vs CapacityStrip's small segmented row). */}
        <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr_1fr]">
          <Card>
            <CardContent className="flex h-full flex-col py-4">
              <p className="mb-3 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                Today's capacity
              </p>
              {/* Segmented capacity strip — Committed / Protected / Free per
                  the prototype. CapacityStrip is the existing shared
                  component used by the EA surface; the semicircle gauge
                  treatment that briefly lived here drifted from spec. */}
              <CapacityStrip
                capacity={capacity ? deriveCapacity(capacity) : null}
                loading={loading}
              />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex h-full flex-col py-4">
              <p className="mb-3 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                Focus protected this week
              </p>
              <p className="font-mono text-[26px] font-bold tracking-[-0.01em]">
                {weekFocusProtected}h{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  of {focusTargetHours}h target
                </span>
              </p>
              <Progress
                value={Math.min(100, (weekFocusProtected / focusTargetHours) * 100)}
                className="mt-auto h-1.5"
              />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex h-full flex-col py-4">
              <p className="mb-3 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                Strategic alignment
              </p>
              <p className="font-mono text-[26px] font-bold tracking-[-0.01em]">{alignmentPct}%</p>
              {/* On-plan green for alignment — distinguishes the strategic
                  card from the focus-protected card (blue) above. Hex per
                  spec; #2E7D32 reads as a confident "on plan" tone in
                  both light and dark. */}
              <Progress value={alignmentPct} className="mt-auto h-1.5" indicatorColor="#2E7D32" />
            </CardContent>
          </Card>
        </div>

        {/* Two-column: Today's priorities (left, broader) + Needs attention
            (right). Both surfaces reuse the existing ActiveWorkList and
            DayTuningFeed components — the data hooks are intact, only
            the layout is new. */}
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Target
                  className={cn(
                    'h-4 w-4',
                    activeBlock ? 'text-[#C97A2B]' : 'text-primary',
                  )}
                />
                {activeBlock ? (
                  // Copper accent on the title when we're inside a focus
                  // block — matches the cross-surface "brand/hover" role
                  // (focus = blue, brand = copper) so the change in framing
                  // reads as intentional rather than a state error.
                  <span className="text-[#C97A2B]">
                    Currently focusing on — {activeBlock.label || 'Deep work'}
                  </span>
                ) : (
                  "Today's priorities"
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {activeItems.length === 0
                  ? "Nothing planned for today. Open the plan to schedule what's next."
                  : `${activeItems.length} item${activeItems.length === 1 ? '' : 's'}, ordered by strategic weight.`}
              </p>
            </CardHeader>
            <CardContent className="pt-2">
              {activeBlock && activeBlock.linkedWorkItems && activeBlock.linkedWorkItems.length > 0 ? (
                <>
                  <LinkedItemsList
                    items={activeBlock.linkedWorkItems}
                    blockStartTime={activeBlock.startTime}
                    onOpen={() => navigate('/work-items')}
                  />
                  {laterItems.length > 0 && (
                    <div className="mt-4 border-t border-border pt-3">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                        Later today
                      </p>
                      <ActiveWorkList
                        items={laterItems}
                        loading={loading}
                        onOpen={() => navigate('/work-items')}
                        onComplete={handleCompleteWorkItem}
                      />
                    </div>
                  )}
                </>
              ) : (
                <ActiveWorkList
                  items={activeItems}
                  loading={loading}
                  onOpen={() => navigate('/work-items')}
                  onComplete={handleCompleteWorkItem}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Needs attention
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Coaching prompts and calendar conflicts in one feed.
              </p>
            </CardHeader>
            <CardContent className="pt-2">
              <DayTuningFeed
                entries={tuningEntries}
                loading={loading}
                onAction={(entry) => entry.action && navigate(entry.action.path)}
                onDismiss={dismissTuning}
              />
            </CardContent>
          </Card>
        </div>

        {/* Full-width Strategic Pulse band — gauges instead of bars. */}
        <StrategicPulseBand drivers={drivers} alignmentPct={alignmentPct} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Active Work list — prototype row schema: mono HH:MM start time on the left
// (42px column), title in the middle with the strategic-driver dot inline,
// driver name + type + duration on a meta line below, and a "High" chip on
// the right rendered only when priority === High. Medium / Low intentionally
// render no chip — the spec uses chip density to surface the things you
// actually need to triage today.
// ---------------------------------------------------------------------------

function formatDuration(minutes: number): string {
  if (!minutes || minutes < 1) return '';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

// ---------------------------------------------------------------------------
// LinkedItemsList — slim row schema for the items planned inside the
// *active* focus block. Renders above ActiveWorkList when activeBlock is
// non-null. LinkedWorkItemSummary is a deliberately leaner shape than
// WorkItem (no scheduledBlocks, no nested strategicDriver) so we render a
// minimal row: title + driver dot/name + type + duration. Start time
// defaults to the block's startTime when shown.
// ---------------------------------------------------------------------------

function LinkedItemsList({
  items,
  blockStartTime,
  onOpen,
}: {
  items: LinkedWorkItemSummary[];
  blockStartTime: string;
  onOpen: () => void;
}) {
  // Trim seconds off the block start (HH:MM) for the leading time column.
  const shortStart = blockStartTime ? blockStartTime.slice(0, 5) : '—';
  return (
    <ul className="divide-y divide-border/40">
      {items.map(item => {
        const driverColor = colourForDriverId(item.strategicDriverId ?? undefined);
        const driverName = item.strategicDriverTitle || null;
        const duration = formatDuration(item.estimatedMinutes ?? 0);
        return (
          <li
            key={item.id}
            onClick={onOpen}
            className="grid cursor-pointer grid-cols-[42px_1fr_auto] items-baseline gap-3 px-1 py-2.5 text-sm transition-colors hover:bg-accent/30"
          >
            <span className="font-mono text-[13px] tabular-nums text-muted-foreground">
              {shortStart}
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-[14.5px] font-medium text-foreground">
                  {item.title}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12.5px] text-muted-foreground">
                {driverName && (
                  <>
                    <span
                      aria-hidden
                      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: driverColor }}
                    />
                    <span className="truncate">{driverName}</span>
                    <span aria-hidden className="text-muted-foreground/60">·</span>
                  </>
                )}
                <span>{item.type}</span>
                {duration && (
                  <>
                    <span aria-hidden className="text-muted-foreground/60">·</span>
                    <span>{duration}</span>
                  </>
                )}
              </div>
            </div>
            {item.priority === 'High' ? (
              <Badge variant="danger" className="self-center justify-self-end">High</Badge>
            ) : (
              <span aria-hidden />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ActiveWorkList({
  items,
  loading,
  onOpen,
}: {
  items: WorkItem[];
  loading: boolean;
  onOpen: () => void;
  // onComplete kept in the prop type for callers that still pass it; the
  // prototype row schema moves complete affordances off the dashboard row
  // and back into the Work Items page where bulk actions live.
  onComplete?: (id: string) => void | Promise<void>;
}) {
  if (!loading && items.length === 0) {
    return (
      <div className="py-6 text-center">
        <CheckCircle2 className="mx-auto h-7 w-7 text-emerald-500" />
        <p className="mt-2 text-sm font-medium">Nothing active for today.</p>
        <p className="text-xs text-muted-foreground">
          Plan your day to populate — or add a work item.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <ul className="divide-y divide-border/40">
        {items.map(item => {
          const driverColor = colourForDriverId(item.strategicDriverId);
          const driverName = item.strategicDriver?.title || null;
          const startTime = item.scheduledBlocks?.[0]?.startTime ?? null;
          const duration = formatDuration(item.estimatedMinutes);
          return (
            <li
              key={item.id}
              onClick={onOpen}
              className="grid cursor-pointer grid-cols-[42px_1fr_auto] items-baseline gap-3 px-1 py-2.5 text-sm transition-colors hover:bg-accent/30"
            >
              {/* HH:MM mono start time. 42px fixed column so titles align
                  regardless of whether a row has a time yet. */}
              <span className="font-mono text-[13px] tabular-nums text-muted-foreground">
                {startTime ?? '—'}
              </span>
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-[14.5px] font-medium text-foreground">
                    {item.title}
                  </span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12.5px] text-muted-foreground">
                  {driverName && (
                    <>
                      <span
                        aria-hidden
                        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: driverColor }}
                      />
                      <span className="truncate">{driverName}</span>
                      <span aria-hidden className="text-muted-foreground/60">·</span>
                    </>
                  )}
                  <span>{item.type}</span>
                  {duration && (
                    <>
                      <span aria-hidden className="text-muted-foreground/60">·</span>
                      <span>{duration}</span>
                    </>
                  )}
                </div>
              </div>
              {item.priority === 'High' ? (
                <Badge variant="danger" className="self-center justify-self-end">High</Badge>
              ) : (
                <span aria-hidden />
              )}
            </li>
          );
        })}
      </ul>
      <Button
        variant="ghost"
        size="sm"
        onClick={onOpen}
        className="mt-2 self-start px-2"
      >
        View all work items
        <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Needs Attention feed — calendar conflicts only. The previous treatment
// also surfaced coaching prompts here, but the prototype draws a strict
// line: this card is for **things that need triage** (breaches +
// at-risk), and coaching insights belong in a separate band. Filtering
// out 'coaching' kind upstream keeps the data wire intact for other
// surfaces. Rows are click-to-act — click anywhere on the row to open
// the linked plan/calendar destination. No Ignore button; dismissals
// happen on the detail surface.
// ---------------------------------------------------------------------------

function DayTuningFeed({
  entries,
  loading,
  onAction,
}: {
  entries: DayTuningEntry[];
  loading: boolean;
  onAction: (entry: DayTuningEntry) => void;
  // onDismiss kept in props for compatibility with callers that still
  // pass it; the dashboard row no longer renders an Ignore button.
  onDismiss?: (id: string) => void;
}) {
  // Drop coaching from this surface — it belongs in the Strategic Pulse
  // coaching insight slot, not here. Spec is alert (breach) + flag
  // (at-risk) only.
  const triageEntries = useMemo(
    () => entries.filter(e => e.kind !== 'coaching'),
    [entries],
  );

  if (!loading && triageEntries.length === 0) {
    return (
      <div className="py-4 text-center">
        <CheckCircle2 className="mx-auto h-6 w-6 text-emerald-500" />
        <p className="mt-1.5 text-sm font-medium">Nothing needs attention.</p>
        <p className="text-xs text-muted-foreground">
          No conflicts or breaches on today's plan.
        </p>
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {triageEntries.map(entry => {
        const isBreach = entry.kind === 'conflict-hard';
        const palette = isBreach
          ? 'bg-rose-50/40 dark:bg-rose-500/10'
          : 'bg-amber-50/50 dark:bg-amber-500/10';
        const Icon = isBreach ? AlertTriangle : Flag;
        const iconColor = isBreach ? 'text-rose-500' : 'text-amber-500';
        return (
          <li
            key={entry.id}
            onClick={() => entry.action && onAction(entry)}
            className={cn(
              'flex items-start gap-2.5 rounded-[10px] p-3 text-xs transition-colors',
              palette,
              entry.action && 'cursor-pointer hover:brightness-105',
            )}
          >
            <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconColor)} />
            <div className="min-w-0 flex-1">
              <p className="text-[13.5px] font-semibold leading-tight">{entry.title}</p>
              <p className="mt-0.5 text-[12.5px] leading-[1.45] text-muted-foreground">{entry.detail}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
